import { UNSAVED_EDIT_STATUS } from 'app/App/const';
import {
  createDuplicateComponent,
  deleteComponentFailure,
  deleteComponentRequest,
  getExistingComponent,
  getExistingComponentFailure,
  openComponentForEditing
} from 'app/CanvasComponent/actions';
import editStatusSelector from 'app/App/selectors/editStatus';
import { fetchExperiment } from 'app/Experiments/thunks';
import { isExperiment } from 'app/Experiments/utils';
import { notifyAlert, notifySuccess, notifyFailure } from 'app/Notifications/actions';
import { addComponentToPage, removeActivePage } from 'app/Page/actions';
import { visitComponent } from 'app/Router/actions';
import { showUnsavedChangesConfirmationModal } from 'app/UnsavedChangesConfirmationModal/actions';
import { clearContentOrFolderInStore } from 'app/UserInfo/actions';
import lanIdSelector from 'app/UserInfo/selectors/lanId';
import { CONTENT_TYPES } from 'lib/const';
import { EN } from 'lib/locales';
import { isBlank } from 'lib/string';
import { PUBLISHED } from 'lib/versions';
import {
  UI_ADD_NEW_COMPONENT_TO_PAGE,
  UI_ADD_MASTER_COMPONENT_TO_PAGE,
  duplicateComponent
} from 'utils/factory';

/**
 * checks the edit status and dispatches actions
 * @param {Array} actions - to be dispatched
 * @returns {undefined}
 */
export const checkStatusAndDispatchActionsThunk = actions => (dispatch, getState) => {
  const editStatus = editStatusSelector(getState());
  if (UNSAVED_EDIT_STATUS.includes(editStatus)) {
    dispatch(showUnsavedChangesConfirmationModal(actions, actions));
  } else {
    actions.forEach(action => {
      dispatch(action);
    });
  }
};

/**
 * thunk to add existing master component to page
 * @param {object} data - data for thunk
 * @param {object} data.component - component to be added
 * @param {boolean} data.openForEditing - open the component for editing
 * @returns {Function} thunk
 */
export const addComponentToPageThunk = ({ component, openForEditing = true }) => dispatch => {
  const componentPosition = window.sessionStorage.getItem('componentPosition');
  const position = parseInt(componentPosition, 10);
  dispatch(addComponentToPage({ component, position, openForEditing }));
  window.sessionStorage.removeItem('componentPosition');

  if (openForEditing) {
    dispatch(openComponentForEditing(component));
  }
};

/**
 * thunk to add existing master component to page
 * request component and switch mode
 * @param {object} params - function params
 * @param {string} params.id - new page uuid
 * @param {boolean} params.openForEditing - open the component for editing
 * @returns {Function} thunk
 */
export const addMasterComponentToPageThunk = ({ id, openForEditing = true }) => async (
  dispatch,
  _,
  { api }
) => {
  const version = PUBLISHED;
  const locale = EN;
  dispatch(notifyAlert('Adding Master Component to Page'));
  try {
    dispatch(getExistingComponent(id, version));
    const component = await api.component.getComponent({ id, version, locale });
    component.uiAction = UI_ADD_MASTER_COMPONENT_TO_PAGE;
    dispatch(addComponentToPageThunk({ component, openForEditing }));

    if (isExperiment(component) && !isBlank(component.Payload.experimentName)) {
      dispatch(fetchExperiment(component.Payload.experimentName));
    }

    dispatch(
      notifySuccess(`Component ${component.ComponentName} has been added to the page.`)
    );
  } catch (error) {
    dispatch(getExistingComponentFailure(id, error));
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to delete the component
 * @param {string} id - to be deleted component uuid
 * @param {string} componentName - to be deleted component name
 * @returns {Function} thunk
 */
export const deleteComponentThunk = (id, componentName) => async (dispatch, _, { api }) => {
  dispatch(notifyAlert('Deleting component'));
  try {
    dispatch(deleteComponentRequest());
    await api.component.deleteComponent(id);
    dispatch(clearContentOrFolderInStore(CONTENT_TYPES.COMPONENT, id));
    dispatch(notifySuccess(`Component delete request for '${componentName}' is completed.`));
  } catch (error) {
    dispatch(deleteComponentFailure(error));
    dispatch(
      notifyFailure(error.message || 'Component delete process completed with errors!')
    );
  }
};

/**
 * thunk to execute given function on selected search component
 * request component and switch mode
 * @param {object} selected - search component object with Id and ComponentName
 * @param {Function} functionToExecuteOnComponent - function needs to be executed with selected components data
 * @returns {Function} thunk
 */
export const getComponentAndExecuteFunctionThunk = (
  { Id, ComponentName },
  functionToExecuteOnComponent
) => async (dispatch, _, { api }) => {
  const locale = EN;
  dispatch(notifyAlert(`Adding '${ComponentName}' component.`));
  try {
    const component = await api.component.getComponentLatestVersion({ id: Id, locale });
    functionToExecuteOnComponent(component);
    dispatch(notifySuccess(`Component ${component.ComponentName} has been added.`));
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to add a duplicate component to the page
 * @param {string} id - of the selected component to duplicate
 * @returns {Function} thunk
 */
export const addDuplicateComponentToPageThunk = id => async (dispatch, getState, { api }) => {
  dispatch(notifyAlert('Adding a duplicate Component to Page'));
  try {
    const component = await api.component.getComponentLatestVersion({ id });
    if (component.Id == null) {
      dispatch(
        notifyFailure(
          'Duplication failed. Could not retrieve current component data. Please try again.'
        )
      );
      return;
    }
    const state = getState();
    const lanId = lanIdSelector(state);

    const duplicatedComponent = duplicateComponent({ component, lanId });
    duplicatedComponent.uiAction = UI_ADD_NEW_COMPONENT_TO_PAGE;

    dispatch(addComponentToPageThunk({ component: duplicatedComponent }));
    dispatch(
      notifySuccess(
        `A duplicate component has been add to the page as '${duplicatedComponent.ComponentName}'`
      )
    );
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to add a duplicate component to a container.
 * @param {object} component - search component object with Id and ComponentName
 * @param {Function} functionToExecuteOnComponent - function needs to be executed with duplicate component's data
 * @returns {Function} thunk
 */
export const addDuplicateComponentToContainerThunk = (
  component,
  functionToExecuteOnComponent
) => async (dispatch, getState, { api }) => {
  dispatch(notifyAlert('Creating a duplicate Component'));
  try {
    const latestComponent = await api.component.getComponentLatestVersion({
      id: component.Id
    });
    if (latestComponent.Id == null) {
      dispatch(
        notifyFailure(
          'Duplication failed. Could not retrieve current component data. Please try again.'
        )
      );
      return;
    }
    const state = getState();
    const lanId = lanIdSelector(state);
    const newDuplicateComponent = duplicateComponent({
      lanId,
      component: latestComponent,
      isMaster: true
    });

    dispatch(
      notifySuccess(
        `A duplicate component has been created as '${newDuplicateComponent.ComponentName}'`
      )
    );
    functionToExecuteOnComponent(newDuplicateComponent);
    dispatch(
      notifySuccess(`Component ${newDuplicateComponent.ComponentName} has been added.`)
    );
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to create a duplicate component
 * @param {string} id - of the selected component to duplicate
 * @returns {Function} thunk
 */
export const createDuplicateComponentThunk = id => async (dispatch, getState, { api }) => {
  dispatch(notifyAlert('Creating a duplicate Component'));
  try {
    const component = await api.component.getComponentLatestVersion({ id });
    if (component.Id == null) {
      dispatch(
        notifyFailure(
          'Duplication failed. Could not retrieve current component data. Please try again.'
        )
      );
      return;
    }
    const state = getState();
    const lanId = lanIdSelector(state);
    const newComponent = duplicateComponent({
      component,
      lanId,
      isMaster: true
    });

    dispatch(createDuplicateComponent(newComponent));
    dispatch(
      notifySuccess(
        `A duplicate component has been created as '${newComponent.ComponentName}'`
      )
    );
  } catch (error) {
    dispatch(notifyFailure(error));
  }
};

/**
 * thunk to create duplicate component after checking the edit status
 * @param {string} id - of the selected component
 * @param {string} version - version of the selected component
 * @returns {Function} thunk
 */
export const checkStatusAndDuplicateComponentThunk = (id, version) => async dispatch => {
  dispatch(
    checkStatusAndDispatchActionsThunk([await createDuplicateComponentThunk(id, version)])
  );
};

/**
 * thunk to switch to selected component from page
 * @param {string} id - of the selected component
 * @param {string} version - version of the selected component
 * @returns {Function} thunk
 */
export const switchToComponentThunk = (id, version) => dispatch => {
  dispatch(removeActivePage());
  dispatch(visitComponent(id, version));
};

/**
 * thunk to switch to selected component from page after checking the edit status
 * @param {string} id - of the selected component
 * @param {string} version - version of the selected component
 * @returns {Function} thunk
 */
export const checkStatusAndSwitchToComponentThunk = (id, version) => dispatch => {
  dispatch(checkStatusAndDispatchActionsThunk([switchToComponentThunk(id, version)]));
};
